'use client';

import { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Trash2 } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { formatEstimateAmount } from '@/shared/lib/utils';
import { EstimateUnitSelect } from './EstimateUnitSelect';
import type {
  EstimateItem,
  UnitOption,
  UpdateEstimateItemRequest,
} from '../api/estimate-service';

/**
 * Parse a user-typed decimal field, tolerating `,` as the decimal separator;
 * `null` for empty, garbage, negative or non-finite input (Apple/Android
 * `parseEstimateDecimal` parity — the API validates `min:0`). The explicit
 * empty check matters: `Number('')` is `0`, so without it clearing a field
 * would PATCH a real `0` to the server.
 */
function parseEstimateDecimal(raw: string): number | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed.replace(',', '.'));
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

interface EstimateItemRowProps {
  item: EstimateItem;
  /** Server-driven unit picker options (current measurement system). */
  unitOptions: UnitOption[];
  disabled?: boolean;
  /** Schedule a debounced PATCH for one field of this item. */
  onFieldChange: (itemId: string, data: UpdateEstimateItemRequest) => void;
  /** Flush any pending debounced edit for this item (blur/Enter). */
  onFieldCommit: (itemId: string) => void;
  onDelete: (itemId: string) => void;
  /** This row was just added (add button / delete-undo restore): scroll it
   *  into view and run the flash animation once, then report back via
   *  onJustAddedShown so later renders don't re-trigger it. */
  justAdded?: boolean;
  onJustAddedShown?: () => void;
}

/**
 * One inline-editable estimate line (compact, Apple parity): a borderless name
 * field, then qty / unit / × / unit price / line total — no field labels,
 * numeric inputs right-aligned so digits sit against the unit and prices read
 * as a column of figures. `line_total` is server-computed and read-only.
 *
 * Two responsive arrangements, switched by CONTAINER width (`@min-[40rem]` (640px) on the
 * `@container` items list in EstimateSection — the editor column narrows in
 * two-column mode, so viewport breakpoints would lie):
 * - narrow (phones): two lines — name (+ drag handle) over the figures
 *   cluster (+ delete);
 * - `@min-[40rem]` (640px)+: ONE line — name takes the available space, then the figures
 *   cluster with compacted columns (Apple's 56/72/76pt parity), with the drag
 *   handle and delete at the trailing end (the two inner row divs flatten via
 *   `display: contents` and `order` re-slots the handle/delete).
 *
 * Local draft state mirrors the cached value but lets the user type freely;
 * each keystroke schedules a debounced PATCH and blur flushes it immediately.
 * The row is sortable (dnd-kit) — drag the handle to reorder within the
 * section.
 */
export function EstimateItemRow({
  item,
  unitOptions,
  disabled = false,
  onFieldChange,
  onFieldCommit,
  onDelete,
  justAdded = false,
  onJustAddedShown,
}: EstimateItemRowProps) {
  const t = useTranslations('common.estimate.editor');

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id, disabled });

  // Just-added row: scroll into view once on mount/flag — the flash itself is
  // the CSS animation class below (teal wash holds briefly, then fades).
  const rowRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!justAdded) return;
    rowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [justAdded]);

  // Draft buffers — re-sync when the server value changes (e.g. WS reconcile)
  // and the field isn't being typed into right now.
  const [name, setName] = useState(item.name);
  const [quantity, setQuantity] = useState(String(item.quantity));
  const [unitPrice, setUnitPrice] = useState(String(item.unit_price));

  useEffect(() => setName(item.name), [item.name]);
  useEffect(() => setQuantity(String(item.quantity)), [item.quantity]);
  useEffect(() => setUnitPrice(String(item.unit_price)), [item.unit_price]);

  // Unparseable numeric drafts (garbage, emptied field) flip the focus outline
  // and text to the destructive red while typing; blur reverts the draft to
  // the committed value, so the state never survives the edit session
  // (cross-platform estimate-editor pattern).
  const quantityInvalid = parseEstimateDecimal(quantity) === null;
  const unitPriceInvalid = parseEstimateDecimal(unitPrice) === null;

  const commitName = (value: string) => {
    const trimmed = value.trim();
    if (trimmed && trimmed !== item.name) {
      onFieldChange(item.id, { name: trimmed });
    }
  };

  // A unit pick is discrete — PATCH immediately (no debounce window). The
  // currently displayed quantity and unit price ride along so the visible
  // numbers stay fixed and only their denomination changes (the server
  // interprets both under the NEW unit).
  const commitUnit = (next: string | null) => {
    onFieldChange(item.id, {
      unit: next,
      quantity: parseEstimateDecimal(quantity) ?? item.quantity,
      unit_price: parseEstimateDecimal(unitPrice) ?? item.unit_price,
    });
    onFieldCommit(item.id);
  };

  const commitNumber = (
    raw: string,
    field: 'quantity' | 'unit_price',
    current: number
  ) => {
    const parsed = parseEstimateDecimal(raw);
    if (parsed === null || parsed === current) return;
    onFieldChange(item.id, { [field]: parsed });
  };

  const blurOnEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
  };

  // Name lives in an auto-growing textarea (long names wrap instead of
  // truncating); Enter commits — a line break is never part of a name.
  const blurOnEnterTextarea = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      (e.target as HTMLTextAreaElement).blur();
    }
  };

  return (
    <div
      ref={(node) => {
        setNodeRef(node);
        rowRef.current = node;
      }}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      onAnimationEnd={justAdded ? onJustAddedShown : undefined}
      className={cn(
        'space-y-2 px-4 py-3 @min-[40rem]:flex @min-[40rem]:items-center @min-[40rem]:gap-2 @min-[40rem]:space-y-0',
        isDragging && 'relative z-10 bg-estimate-paper opacity-90 shadow-md',
        justAdded && 'animate-estimate-row-flash'
      )}
    >
      {/* Row 1: name + drag handle. The focused field gets brand-teal text
          plus an active teal outline drawn OUTSIDE its bounds (outline-offset —
          no layout shift; cross-platform estimate-editor pattern).
          At @min-[40rem] the wrapper dissolves (display: contents) into the one-line
          flex row; `order` sends the handle to the trailing end. */}
      <div className="flex items-center gap-2 @min-[40rem]:contents">
        <textarea
          value={name}
          rows={1}
          onChange={(e) => {
            const next = e.target.value.replace(/\n/g, ' ');
            setName(next);
            commitName(next);
          }}
          onBlur={() => {
            // A blanked-out name is never committed — revert the draft.
            if (!name.trim()) setName(item.name);
            onFieldCommit(item.id);
          }}
          onKeyDown={blurOnEnterTextarea}
          disabled={disabled}
          placeholder={t('itemNamePlaceholder')}
          aria-label={t('columns.name')}
          className="min-w-0 flex-1 resize-none rounded-md bg-transparent text-list-item-subtitle field-sizing-content text-foreground outline-none placeholder:text-muted-foreground focus:text-primary focus:outline-[1.5px] focus:outline-offset-2 focus:outline-primary focus:outline-solid disabled:opacity-50"
        />
        <button
          type="button"
          {...attributes}
          {...listeners}
          disabled={disabled}
          aria-label={t('reorderItem')}
          className="inline-flex size-7 shrink-0 cursor-grab touch-none items-center justify-center text-muted-foreground/60 transition-colors hover:text-muted-foreground active:cursor-grabbing disabled:opacity-50 @min-[40rem]:order-1"
        >
          <GripVertical className="size-5" strokeWidth={1.5} />
        </button>
      </div>

      {/* Row 2: ······ qty · unit · × · unit price · line total + delete.
          Names anchor left (row above); the figures cluster right next to the
          line total so the layout reads as a name column on the left and a
          number column on the right. At @min-[40rem] this wrapper dissolves too
          (display: contents) — the figures follow the name on the same line
          with compacted column widths, and `order` sends delete last. The
          text-* classes still inherit through display: contents. */}
      <div className="flex items-center gap-2 text-card-meta text-muted-foreground @min-[40rem]:contents">
        <input
          value={quantity}
          inputMode="decimal"
          onChange={(e) => {
            setQuantity(e.target.value);
            commitNumber(e.target.value, 'quantity', item.quantity);
          }}
          onBlur={() => {
            // An invalid draft (garbage/empty) must not survive the edit
            // session — revert to the committed value (only parsed keystrokes
            // ever reached the cache).
            setQuantity(String(item.quantity));
            onFieldCommit(item.id);
          }}
          onKeyDown={blurOnEnter}
          disabled={disabled}
          aria-label={t('columns.quantity')}
          placeholder={t('columns.quantity')}
          className={cn(
            'ms-auto w-16 rounded-md bg-transparent text-end text-foreground outline-none tabular-nums placeholder:text-muted-foreground focus:outline-[1.5px] focus:outline-offset-2 focus:outline-solid disabled:opacity-50 @min-[40rem]:w-14',
            quantityInvalid
              ? 'focus:text-destructive focus:outline-destructive'
              : 'focus:text-primary focus:outline-primary'
          )}
        />
        <EstimateUnitSelect
          value={item.unit}
          options={unitOptions}
          disabled={disabled}
          onChange={commitUnit}
        />
        <span aria-hidden>×</span>
        <input
          value={unitPrice}
          inputMode="decimal"
          onChange={(e) => {
            setUnitPrice(e.target.value);
            commitNumber(e.target.value, 'unit_price', item.unit_price);
          }}
          onBlur={() => {
            // An invalid draft (garbage/empty) must not survive the edit
            // session — revert to the committed value (only parsed keystrokes
            // ever reached the cache).
            setUnitPrice(String(item.unit_price));
            onFieldCommit(item.id);
          }}
          onKeyDown={blurOnEnter}
          disabled={disabled}
          aria-label={t('columns.unitPrice')}
          placeholder={t('columns.unitPrice')}
          className={cn(
            'w-20 rounded-md bg-transparent text-end text-foreground outline-none tabular-nums placeholder:text-muted-foreground focus:outline-[1.5px] focus:outline-offset-2 focus:outline-solid disabled:opacity-50 @min-[40rem]:w-18',
            unitPriceInvalid
              ? 'focus:text-destructive focus:outline-destructive'
              : 'focus:text-primary focus:outline-primary'
          )}
        />

        {/* Plain amount, no currency — the currency renders only in the
            totals summary (cross-platform convention). Inherits the wrapper's
            text-card-meta size (through display: contents at @min-[40rem]) so
            all digits in the row read at one size. Must never wrap when the
            row gets tight — the flexible inputs compress instead. The uniform
            min-width keeps the total column equal across rows so the "×"
            separators stack on one vertical line. */}
        <span className="ms-2 inline-block min-w-20 whitespace-nowrap text-end text-foreground tabular-nums @min-[40rem]:min-w-19">
          {formatEstimateAmount(item.line_total)}
        </span>
        <button
          type="button"
          onClick={() => onDelete(item.id)}
          disabled={disabled}
          aria-label={t('deleteItem')}
          className="inline-flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:text-destructive disabled:opacity-50 @min-[40rem]:order-2"
        >
          <Trash2 className="size-4" strokeWidth={1.5} />
        </button>
      </div>
    </div>
  );
}
